import { useAppDispatch, useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import MenuDetailModal from 'features/MenuDetail/pages/MenuDetail';
import SubTitleDetailModal from 'features/SubTitleMenu/pages/detail/Detail';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { Accordion, Button, Col, Form, ModalProps, Row, Table } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import 'react-datepicker/dist/react-datepicker.css';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaSort, FaSortDown, FaSortUp, FaSync } from 'react-icons/fa';
import { MENU_SELECTION_FLAG } from '../constants';
import { Menu, SearchSubMenuGroup, SubMenuGroup } from '../models/submenulist';
import { selectSubMenuGroupList, subMenuGroupActions } from '../subMenuSlice';
import DisplaySubMenuGroupDetail from './SubMenuGroupDetail';
import './SubMenuList.scss';

interface FormSearchValueProps {
  cd: string;
  name: string;
}

function SubMenuList() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // selectors
  const subMenuGroupList = useAppSelector(selectSubMenuGroupList);
  const isEditMode = useAppSelector(selectIsEditMode);

  // states
  const [selectedSubMenuGroup, setSelectedSubMenuGroup] = useState<SubMenuGroup | undefined>(undefined);
  const [activePopoverId, setActivePopoverId] = useState<number | null>(null);
  const [subMenuGroups, setSubMenuGroups] = useState<Array<SubMenuGroup>>([]);
  const [sortFilter, setSortFilter] = useState<{ sortName: string; sortBy: string }>({
    sortName: '',
    sortBy: '',
  });
  const [showSubTitleMenuDetailModal, setShowSubTitleMenuDetailModal] = useState(false);
  const [formSearchValue, setFormSearchValue] = useState<FormSearchValueProps>({
    cd: '',
    name: '',
  });

  //ref
  const menuDetailModalRef = useRef<ModalProps>(MenuDetailModal);
  const displaySubMenuGroupDetailModalRef = useRef<ModalProps>(DisplaySubMenuGroupDetail);
  const btnDetail = useRef<HTMLButtonElement>(null);
  const checkboxesRef = useRef<Array<HTMLInputElement>>([]);
  const selectedSubtitleMenuIdRef = useRef<string>('');
  const formSearchRef = useRef<HTMLFormElement>(null);

  // use effect
  useEffect(() => {
    if (subMenuGroupList) {
      const sortedSubMenuGroups = sortData(subMenuGroupList);
      setSubMenuGroups(sortedSubMenuGroups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subMenuGroupList]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    handleSubmitSearch(undefined);

    return () => {
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sortedSubMenuGroups = sortData(subMenuGroupList);
    setSubMenuGroups(sortedSubMenuGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortFilter]);

  const handleOpenModalEditMenu = (menu_id: number, menu_selection_flg: number) => {
    if (menu_selection_flg === MENU_SELECTION_FLAG.REGULAR) {
      if (menuDetailModalRef.current) {
        menuDetailModalRef.current.onReset();
        menuDetailModalRef.current.openModalExternal(menu_id);
      }
    } else if (menu_selection_flg === MENU_SELECTION_FLAG.SUB_TITLE) {
      selectedSubtitleMenuIdRef.current = menu_id.toString();
      setShowSubTitleMenuDetailModal(true);
    }
  };

  const handleDisplaySubMenuGroupDetailModal = () => {
    displaySubMenuGroupDetailModalRef.current.openModal();
    btnDetail.current?.blur();
  };

  // Handle a submit event of the form
  function saveFormSearch() {
    if (formSearchRef.current) {
      setFormSearchValue({
        ...formSearchValue,
        cd: formSearchRef.current['submenugroup_cd'].value.trim(),
        name: formSearchRef.current['submenugroup_name'].value.trim(),
      });
    }
  }

  const handleSubmitSearch = (event: FormEvent<HTMLFormElement> | undefined) => {
    if (formSearchRef.current) {
      const searchCd = (formSearchRef.current['submenugroup_cd'].value.trim() as string) || '';
      const searchName = (formSearchRef.current['submenugroup_name'].value.trim() as string) || '';

      //reset checked box
      checkboxesRef.current.forEach((value, index) => {
        if (value) checkboxesRef.current[index].checked = false;
      });
      //reset selected submenu group
      setSelectedSubMenuGroup(undefined);
      // Prevent the default behavior, as we don't want
      // for our page to reload upon submit.
      if (event) {
        event.preventDefault();
      }
      displaySubMenuGroupDetailModalRef.current.onReset();
      let searchSubMenuGroup = {
        group_cd: searchCd,
        group_name: searchName,
      } as SearchSubMenuGroup;
      dispatch(subMenuGroupActions.fetchSubMenuGroup(searchSubMenuGroup));
    }
  };

  const handleCheckedSubMenuGroup = (event: ChangeEvent<HTMLInputElement>) => {
    const id = event.currentTarget.id;
    if (event.currentTarget.checked === true) {
      const isChecked = checkboxesRef.current.find(
        (checkbox) => checkbox && checkbox.id === id && checkbox.checked === true
      );
      if (isChecked) {
        const subMenuGroup = subMenuGroups.find((s: SubMenuGroup) => s.id === parseInt(id));
        if (subMenuGroup) {
          setSelectedSubMenuGroup(subMenuGroup);
        } else {
          setSelectedSubMenuGroup(undefined);
        }
        //uncheck another checkbox
        checkboxesRef.current.forEach((value, index) => {
          if (value && value.id !== isChecked.id) {
            value.checked = false;
          }
        });
      }
    } else {
      setSelectedSubMenuGroup(undefined);
    }
  };

  const handleUpdateSubmenuGroup = (subMenuGroupEdit: SubMenuGroup | undefined) => {
    if (subMenuGroupEdit) {
      if (subMenuGroups && subMenuGroups.length > 0) {
        let newSubMenuGroups = subMenuGroups?.map((obj) => {
          if (obj.id === subMenuGroupEdit.id) {
            return { ...obj, ...subMenuGroupEdit };
          }
          return obj;
        });
        newSubMenuGroups = sortData(newSubMenuGroups);
        setSubMenuGroups(newSubMenuGroups);
      }
    }
  };

  const handleMouseEnter = (id: number) => {
    setActivePopoverId(id);
  };

  const handleMouseLeave = () => {
    setActivePopoverId(null);
  };

  const sortData = (arrSubMenuGroup: SubMenuGroup[]) => {
    const sortedArray = [...arrSubMenuGroup];
    if (sortFilter.sortName === '' || sortFilter.sortBy === '') {
      return sortedArray;
    }

    if (sortFilter.sortName === 'cd') {
      sortedArray.sort((a, b) => {
        const cdA = Number(a.cd) || 0;
        const cdB = Number(b.cd) || 0;
        if (sortFilter.sortBy === 'asc') {
          return cdA - cdB;
        } else {
          return cdB - cdA;
        }
      });
    }

    if (sortFilter.sortName === 'name') {
      sortedArray.sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        if (sortFilter.sortBy === 'asc') {
          return nameA.localeCompare(nameB, 'ja');
        } else {
          return nameB.localeCompare(nameA, 'ja');
        }
      });
    }

    if (sortFilter.sortName === 'type') {
      sortedArray.sort((a, b) => {
        const typeA = a.menu_selection_flg;
        const typeB = b.menu_selection_flg;
        if (sortFilter.sortBy === 'asc') {
          return typeA - typeB;
        } else {
          return typeB - typeA;
        }
      });
    }

    if (sortFilter.sortName === 'note') {
      sortedArray.sort((a, b) => {
        const noteA = a.note || '';
        const noteB = b.note || '';
        if (sortFilter.sortBy === 'asc') {
          return noteA.localeCompare(noteB, 'ja');
        } else {
          return noteB.localeCompare(noteA, 'ja');
        }
      });
    }

    return sortedArray;
  };

  const handleSortByColumn = (columnName: string) => {
    let sortName = columnName;
    let sortBy = 'asc';
    if (sortFilter.sortName === columnName) {
      if (sortFilter.sortBy === 'asc') {
        sortBy = 'desc';
      } else if (sortFilter.sortBy === 'desc') {
        sortBy = '';
      }
    }

    setSortFilter({
      sortName: sortName,
      sortBy: sortBy,
    });
  };

  //util
  const concatString = (menus: Menu[]) => {
    let result = '';
    if (menus && menus.length > 0) {
      for (let i = 0; i < menus.length; i++) {
        result = result.concat(menus[i].food_system_menu.name);
        if (i < menus.length - 1) {
          result = result.concat(', ');
        }
      }
    }
    return result;
  };

  const handleCloseSubTitleMenuDetailModal = () => {
    setShowSubTitleMenuDetailModal(false);
    selectedSubtitleMenuIdRef.current = '';
  };

  return (
    <>
      {showSubTitleMenuDetailModal &&
        createPortal(
          <SubTitleDetailModal
            id={selectedSubtitleMenuIdRef.current}
            show={true}
            onClose={handleCloseSubTitleMenuDetailModal}
          />,
          document.body
        )}
      <MenuDetailModal ref={menuDetailModalRef} />
      <DisplaySubMenuGroupDetail
        ref={displaySubMenuGroupDetailModalRef}
        subMenuGroup={selectedSubMenuGroup}
        onUpdateSubmenuGroup={handleUpdateSubmenuGroup}
      />
      <div className="page-wrapper">
        <div className="page-header">
          <StatusHeader />
        </div>
        <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
          <div className="page-content store-setting-page d-flex flex-column">
            <div className="mb-3">
              <FeatureTitleBar title={t('SubMenuGroupListFeature.Text.list_of_submenu_groups')} />
            </div>
            <form ref={formSearchRef} className=" mb-3" onSubmit={handleSubmitSearch}>
              <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>{t('MenuBookListPage.search')}</Accordion.Header>
                  <Accordion.Body onExited={saveFormSearch}>
                    <Row className="mb-3">
                      <Col sm={3}>
                        <Row>
                          <Col sm={3}>
                            <Form.Label htmlFor="txt-submenugroup-cd" className="col-form-label">
                              {t('SubMenuGroupListFeature.Text.cd')}:
                            </Form.Label>
                          </Col>
                          <Col sm={6}>
                            <Form.Control id="txt-submenugroup-cd" name="submenugroup_cd" />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                    <Row className="mb-0">
                      <Col sm={3}>
                        <Row>
                          <Col sm={3}>
                            <Form.Label htmlFor="txt-submenugroup-name" className="col-form-label">
                              {t('SubMenuGroupListFeature.Text.name')}:
                            </Form.Label>
                          </Col>
                          <Col sm={6}>
                            <Form.Control id="txt-submenugroup-name" name="submenugroup_name" />
                          </Col>
                        </Row>
                      </Col>
                      <Col sm={9} className="text-end">
                        <Button type="submit">{t('SubMenuGroupListFeature.Component.search')}</Button>
                      </Col>
                    </Row>
                  </Accordion.Body>
                  <div className="accordion-body accordion-summary">
                    <Row>
                      <Col sm={'auto'}>
                        <Form.Label className="col-form-label">{t('SubMenuGroupListFeature.Text.cd')}：</Form.Label>
                        <Form.Label className="col-form-label">{formSearchValue.cd || '-'}</Form.Label>
                      </Col>
                      <Col sm={'auto'}>
                        <Form.Label className="col-form-label">{t('SubMenuGroupListFeature.Text.name')}：</Form.Label>
                        <Form.Label className="col-form-label">{formSearchValue.name || '-'}</Form.Label>
                      </Col>
                    </Row>
                  </div>
                </Accordion.Item>
              </Accordion>
            </form>

            <Row className="mb-3">
              <Col className="d-flex justify-content-start">
                <form onSubmit={handleSubmitSearch}>
                  <div className="algin-items-center">
                    <button type="submit" className="d-flex align-items-center btn p-0">
                      <div className="btn-primary btn-circle d-flex align-items-center justify-content-center me-1">
                        <FaSync />
                      </div>
                      <span className="text-primary">{t('SubMenuGroupListFeature.Component.reload')}</span>
                    </button>
                  </div>
                </form>
              </Col>
              <Col className="d-flex justify-content-end">
                <button
                  type="button"
                  ref={btnDetail}
                  disabled={selectedSubMenuGroup === undefined}
                  className="btn btn-sm btn-primary"
                  onClick={handleDisplaySubMenuGroupDetailModal}
                >
                  {t('SubMenuGroupListFeature.Component.detail')}
                </button>
              </Col>
            </Row>

            <div className="sticky-table flex-grow-1 flex-shrink-1">
              <Table hover>
                <thead style={{ backgroundColor: '#E1D5E7' }}>
                  <tr>
                    <th style={{ width: '4%' }}>{t('SubMenuGroupListFeature.Table.selection')}</th>
                    <th style={{ width: '6%', cursor: 'pointer' }} onClick={handleSortByColumn.bind(null, 'cd')}>
                      <div className="d-flex justify-content-center align-items-center">
                        <span className="d-inline-block flex-fill text-center">
                          {t('SubMenuGroupListFeature.Table.group_cd')}
                        </span>
                        {sortFilter.sortName !== 'cd' || (sortFilter.sortName === 'cd' && sortFilter.sortBy === '') ? (
                          <FaSort className="fs-4" />
                        ) : null}
                        {sortFilter.sortName === 'cd' && sortFilter.sortBy === 'asc' ? (
                          <FaSortUp className="fs-4" />
                        ) : null}
                        {sortFilter.sortName === 'cd' && sortFilter.sortBy === 'desc' ? (
                          <FaSortDown className="fs-4" />
                        ) : null}
                      </div>
                    </th>
                    <th style={{ width: '20%', cursor: 'pointer' }} onClick={handleSortByColumn.bind(null, 'name')}>
                      <div className="d-flex justify-content-center align-items-center">
                        <span className="d-inline-block flex-fill text-center">
                          {t('SubMenuGroupListFeature.Table.group_name')}
                        </span>
                        {sortFilter.sortName !== 'name' ||
                        (sortFilter.sortName === 'name' && sortFilter.sortBy === '') ? (
                          <FaSort className="fs-4" />
                        ) : null}
                        {sortFilter.sortName === 'name' && sortFilter.sortBy === 'asc' ? (
                          <FaSortUp className="fs-4" />
                        ) : null}
                        {sortFilter.sortName === 'name' && sortFilter.sortBy === 'desc' ? (
                          <FaSortDown className="fs-4" />
                        ) : null}
                      </div>
                    </th>
                    <th style={{ width: '20%', cursor: 'pointer' }} onClick={handleSortByColumn.bind(null, 'type')}>
                      <div className="d-flex justify-content-center align-items-center">
                        <span className="d-inline-block flex-fill text-center">
                          {t('SubMenuGroupListFeature.Table.menu_type')}
                        </span>
                        {sortFilter.sortName !== 'type' ||
                        (sortFilter.sortName === 'type' && sortFilter.sortBy === '') ? (
                          <FaSort className="fs-4" />
                        ) : null}
                        {sortFilter.sortName === 'type' && sortFilter.sortBy === 'asc' ? (
                          <FaSortUp className="fs-4" />
                        ) : null}
                        {sortFilter.sortName === 'type' && sortFilter.sortBy === 'desc' ? (
                          <FaSortDown className="fs-4" />
                        ) : null}
                      </div>
                    </th>
                    <th style={{ width: '20%' }}>{t('SubMenuGroupListFeature.Table.assigned_menu')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={handleSortByColumn.bind(null, 'note')}>
                      <div className="d-flex justify-content-center align-items-center">
                        <span className="d-inline-block flex-fill text-center">
                          {t('SubMenuGroupListFeature.Table.remarks')}
                        </span>
                        {sortFilter.sortName !== 'note' ||
                        (sortFilter.sortName === 'note' && sortFilter.sortBy === '') ? (
                          <FaSort className="fs-4" />
                        ) : null}
                        {sortFilter.sortName === 'note' && sortFilter.sortBy === 'asc' ? (
                          <FaSortUp className="fs-4" />
                        ) : null}
                        {sortFilter.sortName === 'note' && sortFilter.sortBy === 'desc' ? (
                          <FaSortDown className="fs-4" />
                        ) : null}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {subMenuGroups && subMenuGroups.length > 0 ? (
                    subMenuGroups.map((subMenuGroup, index) => {
                      return (
                        <tr key={index} className="text-center">
                          <td>
                            <Form.Check
                              aria-label="option 1"
                              style={{ fontSize: '1.5rem' /* other styles */ }}
                              id={subMenuGroup.id.toString()}
                              defaultChecked={false}
                              onChange={handleCheckedSubMenuGroup}
                              ref={(element: HTMLInputElement) => {
                                checkboxesRef.current[subMenuGroup.id] = element;
                              }}
                            />
                          </td>
                          <td>{subMenuGroup.cd}</td>
                          <td>{subMenuGroup.name}</td>
                          <td>
                            {subMenuGroup.menu_selection_flg === MENU_SELECTION_FLAG.REGULAR
                              ? t('SubMenuGroupListFeature.Text.sub_menu')
                              : t('SubMenuGroupListFeature.Text.sub_title_menu')}
                          </td>
                          <td className="position-relative">
                            <div
                              className="position-absolute d-flex align-items-center justify-content-center text-center"
                              style={{ width: '100%', height: '100%', top: 0, left: 0 }}
                              key={subMenuGroup.id}
                              onMouseEnter={() => handleMouseEnter(subMenuGroup.id)}
                              onMouseLeave={handleMouseLeave}
                            >
                              <OverlayTrigger
                                trigger={['hover', 'focus']}
                                placement="bottom"
                                delay={{ show: 10, hide: 40 }}
                                show={activePopoverId === subMenuGroup.id}
                                overlay={
                                  <Popover id={subMenuGroup.id.toString()}>
                                    <Popover.Header as="h3">
                                      {t('SubMenuGroupListFeature.Table.assigned_menu')}
                                    </Popover.Header>
                                    <Popover.Body>
                                      {subMenuGroup.sub_menus &&
                                        subMenuGroup.sub_menus.map((menu: Menu, s_index) => (
                                          <button
                                            onClick={() => {
                                              handleOpenModalEditMenu(menu.id, subMenuGroup.menu_selection_flg);
                                            }}
                                            key={s_index}
                                            type="button"
                                            className="btn btn-link"
                                          >
                                            {menu.food_system_menu.name}
                                          </button>
                                        ))}
                                    </Popover.Body>
                                  </Popover>
                                }
                              >
                                <div className="display-dot">{concatString(subMenuGroup.sub_menus)}</div>
                              </OverlayTrigger>
                            </div>
                          </td>
                          <td>{subMenuGroup.note}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr className="text-center">
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default SubMenuList;
